import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import DonutChart from '@/components/charts/DonutChart/DonutChart';
import StackedBarChart from '@/components/charts/StackedBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';

import {
  reportsEndDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import {
  getURLTimeframeData,
  interpolateColorHex
} from '../../ReportsTemplate.utils';
import useStyles from '../ReportsPages.styles';
import { useFetchReportsEventsData } from './EventsPages.hooks';

const EventsPage4 = () => {
  const { classes } = useStyles();
  const [diff] = useAtom(reportsTimeFrameAtom);
  const [endDate] = useAtom(reportsEndDateAtom);

  // Fetch data for network events metrics
  const { data: networkEventsBySensor } = useFetchReportsEventsData({
    groupByField: 'sensor',
    eventType: 'network'
  });
  const { data: networkEventsByType } = useFetchReportsEventsData({
    groupByField: 'type',
    eventType: 'network'
  });

  const singleDaySensorEvents = getURLTimeframeData(
    networkEventsBySensor ?? [],
    'date'
  );
  const singleDayTypeEvents = getURLTimeframeData(
    networkEventsByType ?? [],
    'date'
  );

  const timeFrameCorrectedSensorEvents =
    diff < 7 ? singleDaySensorEvents : networkEventsBySensor;
  const timeFrameCorrectedTypeEvents =
    diff < 7 ? singleDayTypeEvents : networkEventsByType;

  // Network Events by Type
  const networkEventsTypeData = useMemo(() => {
    const groupTopData = (timeFrameCorrectedTypeEvents ?? []).reduce(
      (acc, { count, field_to_group_by }) => {
        if (!acc[field_to_group_by]) {
          acc[field_to_group_by] = { name: field_to_group_by, count: 0 };
        }
        acc[field_to_group_by].count += count;
        return acc;
      },
      {} as Record<string, { name: string; count: number }>
    );

    const sortedTop = (
      Object.values(groupTopData) as Array<{ name: string; count: number }>
    )
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return sortedTop.map((item, index, array) => ({
      ...item,
      color: interpolateColorHex(
        REPORTS_COLORS.NETWORK_EVENTS,
        REPORTS_COLORS.LOG_EVENTS,
        index / Math.max(1, array.length - 1)
      )
    }));
  }, [timeFrameCorrectedTypeEvents]);

  // Network Events by Sensor
  const networkEventsSensorData = useMemo(() => {
    const groupTopData = (timeFrameCorrectedSensorEvents ?? []).reduce(
      (acc, { count, field_to_group_by }) => {
        if (!acc[field_to_group_by]) {
          acc[field_to_group_by] = { name: field_to_group_by, count: 0 };
        }
        acc[field_to_group_by].count += count;
        return acc;
      },
      {} as Record<string, { name: string; count: number }>
    );

    const sortedTop = (
      Object.values(groupTopData) as Array<{ name: string; count: number }>
    )
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return sortedTop.map((item, index, array) => ({
      ...item,
      color: interpolateColorHex(
        REPORTS_COLORS.NETWORK_EVENTS,
        REPORTS_COLORS.LOG_EVENTS,
        index / Math.max(1, array.length - 1)
      )
    }));
  }, [timeFrameCorrectedSensorEvents]);

  const networkEventsByDateBySensor = useMemo(() => {
    if (!networkEventsBySensor?.length) return [];

    const daysToGenerate = Math.max(diff, 7);

    const dateRange = Array.from({ length: daysToGenerate }, (_, i) =>
      dayjs(endDate)
        .subtract(daysToGenerate - 1 - i, 'day')
        .format('YYYY-MM-DD')
    );

    const forwarderSet = new Set<string>();
    const dataLookup = new Map<string, Map<string, number>>();

    networkEventsBySensor.forEach((item) => {
      const dateKey = item.date;
      forwarderSet.add(item.field_to_group_by);

      let dateEntry = dataLookup.get(dateKey);
      if (!dateEntry) {
        dateEntry = new Map();
        dataLookup.set(dateKey, dateEntry);
      }
      dateEntry.set(item.field_to_group_by, item.count);
    });

    const uniqueForwarders = Array.from(forwarderSet);

    return dateRange.map((dateStr) => {
      const dateObj: Record<string, Date | number> = {
        date: dayjs(dateStr).toDate()
      };

      uniqueForwarders.forEach((forwarder) => {
        dateObj[forwarder] = dataLookup.get(dateStr)?.get(forwarder) ?? 0;
      });

      return dateObj;
    });
  }, [networkEventsBySensor, diff, endDate]);

  const networkSensorSeries: AgBarSeriesOptions[] = useMemo(() => {
    if (!networkEventsBySensor?.length) return [];

    const uniqueSensors = [
      ...new Set(networkEventsBySensor.map((item) => item.field_to_group_by))
    ];

    const colorFactor = Math.max(1, uniqueSensors.length - 1);

    return uniqueSensors.map((sensor, index) => ({
      type: 'bar' as const,
      xKey: 'date' as const,
      yKey: sensor,
      yName: sensor,
      stacked: true,
      fill: interpolateColorHex(
        REPORTS_COLORS.LOG_EVENTS,
        REPORTS_COLORS.NETWORK_EVENTS,
        index / colorFactor
      )
    }));
  }, [networkEventsBySensor]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>Network Events</ReportsTemplateSectionTitle>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        <Box sx={{ width: '49%' }}>
          <div data-testid="top-network-events-by-type" style={{ height: 200 }}>
            <Typography className={classes.reportsPreviewSubTitle}>
              TOP NETWORK EVENTS BY TYPE
            </Typography>
            <DonutChart data={networkEventsTypeData} />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.logEventsByVendorDescription}
          </ReportParagraph>
        </Box>
        <div style={{ width: '2%' }}>
          <Box
            sx={{
              backgroundColor: '#D1E2D5',
              width: 2,
              height: '100%'
            }}
          />
        </div>
        <Box sx={{ width: '49%' }}>
          <div
            data-testid="top-network-events-by-sensor"
            style={{ height: 200 }}
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              TOP NETWORK EVENTS BY SENSOR
            </Typography>
            <DonutChart data={networkEventsSensorData} />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.logEventsByProductDescription}
          </ReportParagraph>
        </Box>
      </Box>
      <div
        style={{ height: 300, width: '100%', marginTop: '20px' }}
        className={classes.reportsChartContainer}
        data-testid="events-per-day-by-top-network-sensors"
      >
        <Typography className={classes.reportsPreviewSubTitle}>
          Events Per Day By Top Network Sensors
        </Typography>
        <StackedBarChart
          data={networkEventsByDateBySensor}
          series={networkSensorSeries}
          outlined
        />
      </div>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.eventsPerDayByLogForwarderDescription}
      </ReportParagraph>
    </div>
  );
};

export default EventsPage4;
